import { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { PLANS } from "../../lib/brand";
import {
  useResource, PageTitle, Panel, Loading, ErrorState, EmptyState, Pill, DemoBadge,
  Metric, STATUS_TONE, fmtDate, fmtMoney,
} from "../../components/app/Kit";
import { useAuth } from "../../context/AuthContext";

const PLAN_LOOKUP = {
  starter: { signup: "starter_signup", monthly: "starter_monthly" },
  growth:  { signup: "growth_signup",  monthly: "growth_monthly"  },
  scale:   { signup: "scale_signup",   monthly: "scale_monthly"   },
};

const SUB_STATUS_TONE = {
  active: "green", renewal_pending: "cyan", past_due: "amber",
  cancelled: "dim", trialing: "cyan",
};

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.round(ms / 86400000);
}

export default function Payments() {
  const { org } = useAuth();
  const { data, state, error, reload } = useResource("/invoices");
  const [filter, setFilter] = useState("all");
  const [stripeCfg, setStripeCfg] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [busy, setBusy] = useState(null);

  const loadSubscription = () => api.get("/subscriptions/me")
    .then(({ data }) => setSubscription(data.subscription))
    .catch(() => setSubscription(null));

  useEffect(() => {
    api.get("/payments/config").then(({ data }) => setStripeCfg(data)).catch(() => {});
    loadSubscription();
  }, []);

  const rows = useMemo(() => {
    const list = data || [];
    if (filter === "all") return list;
    return list.filter((i) => i.status === filter);
  }, [data, filter]);

  const totals = useMemo(() => {
    const list = data || [];
    const sum = (s) => list.filter((i) => i.status === s).reduce((a, i) => a + i.amount, 0);
    return { paid: sum("paid"), pending: sum("pending"), overdue: sum("overdue"), refunded: sum("refunded") };
  }, [data]);

  const subscribe = async (planKey, mode = "signup") => {
    const lookup = PLAN_LOOKUP[planKey]?.[mode];
    if (!lookup) return;
    setBusy(`sub-${planKey}-${mode}`);
    try {
      const { data: res } = await api.post("/payments/subscription/checkout", {
        plan_key: lookup, origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(null);
    }
  };

  const payInvoice = async (inv) => {
    setBusy(`inv-${inv.id}`);
    try {
      const { data: res } = await api.post("/payments/invoice/checkout", {
        invoice_id: inv.id, origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(null);
    }
  };

  const markPaid = async (inv) => {
    try {
      await api.patch(`/invoices/${inv.id}`, { status: "paid" });
      toast.success(`${inv.number} marked paid in the CRM.`);
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const stripeReady = stripeCfg?.connected;

  return (
    <div data-testid="payments-page">
      <PageTitle
        testId="payments-title"
        title="Payments"
        sub="Invoices, revenue and subscription billing — powered by Stripe."
        action={<DemoBadge />}
      />

      <div
        data-testid="stripe-notice"
        className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4 ${
          stripeReady ? "border-elv-green/25 bg-elv-green/[0.05]" : "border-amber-300/25 bg-amber-300/[0.05]"
        }`}
      >
        <p className={`text-xs ${stripeReady ? "text-elv-text" : "text-amber-200"}`}>
          {stripeReady ? (
            <>
              <span className="font-medium text-elv-green">Stripe checkout is live</span> — running in <span className="uppercase">{stripeCfg?.mode}</span> mode.
              Use test card <span className="font-mono">4242 4242 4242 4242</span>, any future expiry, any CVC.
            </>
          ) : (
            <>
              <span className="font-medium">Stripe not configured.</span> Invoices are recorded inside Elvoria AI only until Stripe is connected.
            </>
          )}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Metric testId="payments-paid" label="Paid" value={totals.paid} hint="Invoices marked paid" />
        <Metric testId="payments-pending" label="Pending" value={totals.pending} tone="cyan" />
        <Metric testId="payments-overdue" label="Overdue" value={totals.overdue} />
        <Metric testId="payments-refunded" label="Refunded" value={totals.refunded} tone="cyan" />
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {["all", "paid", "pending", "overdue", "refunded"].map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)} data-testid={`payments-filter-${f}`}
                  className={`rounded-md border px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] ${
                    filter === f ? "border-elv-green/35 bg-elv-green/[0.08] text-elv-green" : "border-elv-line bg-white/[0.02] text-elv-dim"
                  }`}>
            {f}
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.3fr_1fr]">
        <div>
          {state === "loading" ? <Loading rows={7} testId="payments-loading" /> : null}
          {state === "error" ? <ErrorState message={error} onRetry={reload} testId="payments-error" /> : null}
          {state === "ready" && rows.length === 0 ? (
            <EmptyState icon="CreditCard" title="No invoices" sub="Invoices raised for your customers will appear here." testId="payments-empty" />
          ) : null}
          {state === "ready" && rows.length > 0 ? (
            <div className="grid gap-2">
              {rows.map((i) => (
                <div key={i.id} data-testid={`invoice-${i.id}`} className="glass flex flex-wrap items-center justify-between gap-3 rounded-lg p-4">
                  <div>
                    <p className="font-mono text-xs text-elv-text">{i.number}</p>
                    <p className="mt-1 text-[11px] text-elv-dim">
                      {i.customer_name} · issued {fmtDate(i.issued_at)} · due {fmtDate(i.due_at)}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm text-elv-text">{fmtMoney(i.amount)}</span>
                    <Pill tone={STATUS_TONE[i.status] || "dim"}>{i.status}</Pill>
                    {i.status !== "paid" && stripeReady ? (
                      <button type="button" onClick={() => payInvoice(i)} disabled={busy === `inv-${i.id}`}
                              data-testid={`invoice-pay-${i.id}`}
                              className="rounded-md bg-elv-green px-3 py-1.5 text-[11px] font-semibold text-[#04120A] disabled:opacity-60">
                        {busy === `inv-${i.id}` ? "Opening…" : "Pay with Stripe"}
                      </button>
                    ) : null}
                    {i.status !== "paid" ? (
                      <button type="button" onClick={() => markPaid(i)} data-testid={`invoice-mark-paid-${i.id}`}
                              className="rounded-md border border-elv-green/30 px-3 py-1.5 text-[11px] text-elv-green hover:bg-elv-green/10">
                        Mark paid
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <Panel testId="subscription-panel" title="Your Elvoria AI subscription">
          <div className="grid gap-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-elv-dim">Current plan</span>
              <span className="uppercase text-elv-text">{subscription?.plan || org?.plan || "—"}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-elv-dim">Status</span>
              <Pill tone={SUB_STATUS_TONE[subscription?.status || org?.subscription_status] || "dim"}>
                {subscription?.status || org?.subscription_status || "—"}
              </Pill>
            </div>
            {subscription?.current_period_end ? (
              <div className="flex items-center justify-between text-xs">
                <span className="text-elv-dim">
                  {subscription?.cancel_at_period_end ? "Access ends" : "Next renewal"}
                </span>
                <span className="text-elv-text">
                  {fmtDate(subscription.current_period_end)}
                  {(() => {
                    const d = daysUntil(subscription.current_period_end);
                    if (d === null) return null;
                    if (d < 0) return " · overdue";
                    if (d === 0) return " · today";
                    return ` · in ${d} day${d === 1 ? "" : "s"}`;
                  })()}
                </span>
              </div>
            ) : null}
            {subscription?.status === "renewal_pending" ? (
              <div data-testid="renewal-pending-notice"
                   className="rounded-md border border-elv-cyan/25 bg-elv-cyan/[0.06] px-3 py-2 text-[11px] text-elv-text">
                Renewal invoice sent — check your inbox for the Stripe checkout link (deadline {fmtDate(subscription.renewal_deadline)}).
              </div>
            ) : null}
            {subscription?.status === "past_due" ? (
              <div data-testid="past-due-notice"
                   className="rounded-md border border-amber-300/30 bg-amber-300/[0.08] px-3 py-2 text-[11px] text-amber-200">
                Subscription is past due — complete the pending checkout to restore full access.
              </div>
            ) : null}
            {subscription?.status === "active" && !subscription?.cancel_at_period_end ? (
              <button type="button"
                      data-testid="subscription-cancel-btn"
                      disabled={busy === "cancel"}
                      onClick={async () => {
                        setBusy("cancel");
                        try {
                          const { data } = await api.post("/subscriptions/cancel");
                          setSubscription(data.subscription);
                          toast.success("We won't auto-renew — your workspace stays active until the current period ends.");
                        } catch (e) { toast.error(apiError(e)); }
                        finally { setBusy(null); }
                      }}
                      className="mt-1 rounded-md border border-elv-line px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] text-elv-dim hover:text-elv-text">
                Cancel at period end
              </button>
            ) : null}
            {subscription?.cancel_at_period_end && subscription?.status !== "cancelled" ? (
              <button type="button"
                      data-testid="subscription-resume-btn"
                      disabled={busy === "resume"}
                      onClick={async () => {
                        setBusy("resume");
                        try {
                          const { data } = await api.post("/subscriptions/resume");
                          setSubscription(data.subscription);
                          toast.success("Auto-renewal turned back on.");
                        } catch (e) { toast.error(apiError(e)); }
                        finally { setBusy(null); }
                      }}
                      className="mt-1 rounded-md bg-elv-green px-3 py-1.5 text-[11px] font-semibold text-[#04120A]">
                Resume auto-renewal
              </button>
            ) : null}

            <div className="mt-2 grid gap-2 border-t border-elv-line pt-4">
              {PLANS.map((p) => (
                <div key={p.key} data-testid={`subscription-plan-${p.key}`}
                     className={`rounded-md border p-3 ${
                       (subscription?.plan || org?.plan) === p.key ? "border-elv-green/35 bg-elv-green/[0.06]" : "border-white/[0.06] bg-white/[0.02]"
                     }`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-[0.16em] text-elv-text">{p.name}</span>
                    <span className="text-xs text-elv-dim">
                      ${p.price}/mo · ${p.setup} setup
                    </span>
                  </div>
                  <p className="mt-2 text-[11px] text-elv-dim">{p.summary}</p>
                  {stripeReady ? (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      <button type="button" onClick={() => subscribe(p.key, "signup")}
                              disabled={busy === `sub-${p.key}-signup` || p.key === "scale"}
                              data-testid={`subscribe-signup-${p.key}`}
                              className="rounded-md bg-elv-green px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#04120A] disabled:opacity-40">
                        {p.key === "scale" ? "Contact sales" : (busy === `sub-${p.key}-signup` ? "Opening…" : "Subscribe (first + setup)")}
                      </button>
                      <button type="button" onClick={() => subscribe(p.key, "monthly")}
                              disabled={busy === `sub-${p.key}-monthly` || p.key === "scale"}
                              data-testid={`subscribe-monthly-${p.key}`}
                              className="rounded-md border border-elv-line px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] text-elv-dim hover:text-elv-text disabled:opacity-40">
                        Pay one month
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
            <p className="mt-2 text-[10px] text-elv-dim">
              Auto-renewal: on the last day of your period we email your owner a fresh Stripe checkout link. Two-clicks and the next month is live — no card-on-file surprises. Cancel any time to stop the next invoice.
            </p>
            {!stripeReady ? (
              <p className="mt-2 flex items-start gap-2 text-[11px] text-elv-dim">
                <Icons.Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-elv-cyan" />
                Stripe is not configured yet. Contact us to change plans in the meantime.
              </p>
            ) : null}
          </div>
        </Panel>
      </div>
    </div>
  );
}
